import React, { useState } from 'react';
import { translate } from '../../Services/translation';
import BuyButtonPack from '../BuyButtonPack/BuyButtonPack';
import BuyButton from '../BuyButton';
import StackedPricesLink from '../StackedPrices/StackedPricesLink';
import ProductStock from './ProductStock';
import VariantsPicker from './ProductCardVariantsPicker';
import clsx from 'clsx';

const ProductPrice = ({
  price,
  currentPriceFormatted,
  basePriceFormatted,
  className,
}) => {
  return (
    <div className={`product-list-item__price-container ${className}`}>
      <span className="product-list-item__price">{currentPriceFormatted}</span>
      {basePriceFormatted && (
        <span className="product-list-item__base-price">
          {translate('product.baseprice-prefix')}{' '}
          <span className="product-list-item__base-price__value">
            {basePriceFormatted}
          </span>
        </span>
      )}
      <meta itemProp="price" content={price.price.price} />
      <meta itemProp="priceCurrency" content={price.currency.id} />
    </div>
  );
};

const ProductTags = ({ className, tags, id }) => {
  return tags.length > 0 ? (
    <div className={`product-list-item__tag-container ${className}`}>
      {tags.map((tag, i) => (
        <span
          key={id + i}
          className={`product-list-item__tag product-tag product-tag--${tag}`}
        >
          {translate('product.tag-' + tag)}
        </span>
      ))}
    </div>
  ) : (
    ''
  );
};

const ProductListItem = (props) => {
  const {
    name,
    url,
    images,
    price,
    reactTierPriceItems,
    basePrice,
    basePriceFormatted,
    currentPriceFormatted,
    id,
    erpId,
    packSize,
    tags,
    isInStock,
    hasLowStock,
    stockStatusDescription,
    showBuyButton,
    showQuantityField,
    useVariantUrl,
    type,
    fieldId,
    hasTierPrices,
    variantsPickerViewModel,
  } = props;
  const showPackButton = showBuyButton && showQuantityField && useVariantUrl;
  const [selectedVariant, setSelectedVariant] = useState();
  const onVariantSelect = (variant) => {
    setSelectedVariant(variant);
  };

  const image =
    images && images.length > 0
      ? images[0]
      : '/ui/images/placeholder-image.png';

  const selectedId = selectedVariant?.id ?? id;
  const selectedIsInStock = selectedVariant?.isInStock ?? isInStock;
  const selectedHasLowStock = selectedVariant?.hasLowStock ?? hasLowStock;
  const selectedStockStatusDescription =
    selectedVariant?.stockStatusDescription ?? stockStatusDescription;
  const selectedQuantityFieldId = selectedVariant?.quantityFieldId ?? fieldId;
  const selectedErpId = selectedVariant?.erpId ?? erpId;
  const selectedPackSize = selectedVariant?.packSize ?? packSize;
  const disabledBuyButton = variantsPickerViewModel && !selectedVariant;

  return (
    <div
      itemScope
      itemType="http://schema.org/Product"
      className="product-list-item"
    >
      <article className="product-list-item__container">
        <div className="product-list-item__col product-list-item__col--cols">
          <figure className="product-list-item__image-container">
            <a
              href={url}
              itemProp="url"
              className="product-list-item__image-link"
            >
              <img className="product-list-item__image" src={image} />
            </a>
          </figure>
          <div className="product-list-item__full-width">
            <link itemProp="url" href={url} />
            <meta itemProp="description" content="@Model.Description" />
            <div className="product-list-item__col__stack">
              <div itemProp="name" className="product-list-item__name">
                {name}
              </div>
              <div className="product-list-item__additional-info">
                <span>{selectedErpId}</span>
                {selectedPackSize > 1 && (
                  <span>
                    {selectedPackSize +
                      ' ' +
                      translate('product.packsize-postfix')}
                  </span>
                )}
              </div>
              {price.hasPrice && (
                <ProductPrice
                  className="product-list-item__mobile-only product-list-item__price-container--mobile"
                  price={price}
                  currentPriceFormatted={currentPriceFormatted}
                  basePriceFormatted={basePriceFormatted}
                />
              )}
            </div>

            <ProductTags
              className="product-list-item__col__pull-right"
              tags={tags}
              id={id}
            />
          </div>
        </div>
        <div className="product-list-item__col product-list-item__col--cols product-list-item__col--space-between">
          <link
            itemProp="availability"
            href={`http://schema.org/${isInStock ? 'InStock' : 'OutOfStock'}`}
          />
          <ProductStock
            className="product-list-item__stock-status"
            isInStock={selectedIsInStock}
            hasLowStock={selectedHasLowStock}
            stockStatusDescription={selectedStockStatusDescription}
          />

          <div className="product-list-item__col--tier-prices">
            {hasTierPrices && <StackedPricesLink articleNumber={id} />}
          </div>
        </div>
        <div className="product-list-item__col">
          <div className="product-list-item__col__pull-right">
            {price.hasPrice && (
              <ProductPrice
                className="product-list-item__desktop-only"
                price={price}
                currentPriceFormatted={currentPriceFormatted}
                basePriceFormatted={basePriceFormatted}
              />
            )}

            {showBuyButton && price.hasPrice && (
              <>
                {showPackButton ? (
                  <div
                    className={clsx(
                      'product-list-item__buy-button-container product-list-item__buy-container--with-buy-quantity',
                      variantsPickerViewModel &&
                        'product-list-item__buy-container--with-variants-picker'
                    )}
                  >
                    {variantsPickerViewModel && (
                      <VariantsPicker
                        {...variantsPickerViewModel}
                        onSelect={onVariantSelect}
                        classPrefix={'product-list-item'}
                      />
                    )}
                    <BuyButtonPack
                      articleNumber={selectedId}
                      cssClass={clsx(
                        'btn btn--small btn--wide buy-button-pack__button buy-button product-list-item__buy-button',
                        disabledBuyButton && 'disabled'
                      )}
                      label={translate('product.buy')}
                      packSize={selectedPackSize}
                      quantityFieldId={selectedQuantityFieldId}
                      type="only-wide"
                    />
                  </div>
                ) : (
                  <div
                    className={clsx(
                      'product-list-item__buy-container',
                      variantsPickerViewModel &&
                        'product-list-item__buy-container--with-variants-picker'
                    )}
                  >
                    <input
                      type="hidden"
                      id={selectedQuantityFieldId}
                      value="1"
                    />
                    {variantsPickerViewModel && (
                      <VariantsPicker
                        {...variantsPickerViewModel}
                        onSelect={onVariantSelect}
                        classPrefix={'product-list-item'}
                      />
                    )}
                    <BuyButton
                      articleNumber={selectedId}
                      cssClass={clsx(
                        'btn btn--wide buy-button product-list-item__buy-button',
                        disabledBuyButton && 'disabled'
                      )}
                      label={translate('product.buy')}
                      quantityFieldId={selectedQuantityFieldId}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default ProductListItem;
